import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { S } from "../styles/tokens";
import PlanBanner from "../components/PlanBanner";
import { useEnter } from "../lib/motion";

function DashboardPanel(props) {
  var user = props.user;
  var plan = props.plan;
  var [usage, setUsage] = useState(0);
  var [loading, setLoading] = useState(true);
  var panelRef = useRef(null);
  useEnter(panelRef, { y: 20, stagger: 0.1 });

  var planColors = { free: "#7a6e64", pro: "#a07828", elite: "#5b8aaa", team: "#6b8f71" };
  var planColor = planColors[plan] || planColors.free;
  var thisMonth = new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  var freeLimit = 5;

  useEffect(function() {
    async function fetchUsage() {
      var thisMonthKey = new Date().toISOString().slice(0, 7);
      var { data } = await supabase.from("users").select("analyses_count, analyses_month").eq("id", user.id).single();
      if (data && data.analyses_month === thisMonthKey) {
        setUsage(data.analyses_count || 0);
      } else {
        setUsage(0);
      }
      setLoading(false);
    }
    fetchUsage();
  }, []);

  var planFeatures = {
    free:  ["5 ATS analyses/month", "Basic ATS score", "Keyword analysis"],
    pro:   ["Unlimited ATS analyses", "Full score breakdown", "AI resume rewrite", "Keyword gap analysis"],
    elite: ["Everything in Pro", "AI cover letter", "LinkedIn optimisation", "Priority processing"],
    team:  ["Everything in Elite", "10 team seats", "Bulk resume upload", "API access"],
  };

  return (
    <div ref={panelRef} style={{ maxWidth: 800, margin: "0 auto", padding: "48px 24px 80px" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 9, letterSpacing: 3, color: planColor, marginBottom: 8 }}>YOUR DASHBOARD</div>
        <div style={{ fontFamily: "Georgia,serif", fontSize: 32, fontWeight: 500, color: "#2c2420" }}>
          Welcome back{user.user_metadata && user.user_metadata.full_name ? ", " + user.user_metadata.full_name.split(" ")[0] : ""}!
        </div>
        <div style={{ fontSize: 13, color: "#7a6e64", marginTop: 6 }}>{user.email}</div>
      </div>

      {/* Plan Card */}
      <div style={{ background: "#fefcfa", border: "1.5px solid " + planColor + "44", borderRadius: 20, padding: 28, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 9, letterSpacing: 3, color: "#7a6e64", marginBottom: 8 }}>CURRENT PLAN</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontFamily: "Georgia,serif", fontSize: 28, fontWeight: 500, color: planColor }}>{plan.toUpperCase()}</div>
              <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 700, color: planColor, background: planColor + "18", border: "1px solid " + planColor + "44", padding: "4px 12px", borderRadius: 100 }}>
                {plan === "free" ? "FREE" : "ACTIVE"}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#7a6e64", marginTop: 6 }}>
              {plan === "free" ? "Free plan — 5 analyses/month" :
               plan === "pro" ? "$9/month — billed monthly" :
               plan === "elite" ? "$24/month — billed monthly" :
               "$49/month — up to 10 seats"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {plan === "free" && (
              <button onClick={function() { props.onUpgrade(); }} style={{ background: "#2c2420", color: "#f7f3ee", border: "none", borderRadius: 100, padding: "11px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, cursor: "pointer" }}>
                ⭐ Upgrade Plan
              </button>
            )}
            {plan !== "free" && (
              <>
                <button onClick={function() { props.onAnalyse(); }} style={{ background: planColor, color: "#fefcfa", border: "none", borderRadius: 100, padding: "11px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, cursor: "pointer" }}>
                  Analyse Resume →
                </button>
                <button onClick={async function() {
                  var { data: prof } = await supabase.from("users").select("stripe_customer_id, email").eq("id", user.id).single();
                  try {
                    var res = await fetch("https://syntrix-backend.onrender.com/api/customer-portal", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ customerId: prof && prof.stripe_customer_id, email: user.email }),
                    });
                    var data = await res.json();
                    if (data.url) { window.location.href = data.url; }
                    else { alert("Could not open billing portal. Please contact support."); }
                  } catch (e) {
                    alert("Could not connect to billing portal.");
                  }
                }} style={{ background: "transparent", color: "#7a6e64", border: "1px solid rgba(180,160,140,.35)", borderRadius: 100, padding: "11px 24px", fontFamily: "inherit", fontWeight: 600, fontSize: 12, letterSpacing: 1.5, cursor: "pointer" }}>
                  Manage Billing
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Usage Card — free users only */}
      {plan === "free" && (
        <div style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,0.28)", borderRadius: 20, padding: 28, marginBottom: 20 }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#7a6e64", marginBottom: 16 }}>USAGE THIS MONTH — {thisMonth.toUpperCase()}</div>
          {loading ? (
            <div style={{ fontSize: 12, color: "#7a6e64" }}>Loading...</div>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 12 }}>
                <span style={{ fontFamily: "Georgia,serif", fontSize: 48, fontWeight: 500, color: usage >= freeLimit ? "#b85c38" : "#a07828" }}>{usage}</span>
                <span style={{ fontSize: 16, color: "#7a6e64" }}>/ {freeLimit} analyses used</span>
              </div>
              <div style={{ background: "#e4d9c8", borderRadius: 100, height: 6, overflow: "hidden", marginBottom: 10 }}>
                <div style={{ height: "100%", borderRadius: 100, background: usage >= freeLimit ? "#b85c38" : "#a07828", width: Math.min((usage / freeLimit) * 100, 100) + "%", transition: "width 1s ease" }} />
              </div>
              {usage >= freeLimit ? (
                <div style={{ fontSize: 12, color: "#b85c38" }}>⚠ You've used all your free analyses. <button onClick={props.onUpgrade} style={{ background: "none", border: "none", color: "#b85c38", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Upgrade now</button></div>
              ) : (
                <div style={{ fontSize: 12, color: "#7a6e64" }}>{freeLimit - usage} analyses remaining this month</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Features Card */}
      <div style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,0.28)", borderRadius: 20, padding: 28, marginBottom: 20 }}>
        <div style={{ fontSize: 9, letterSpacing: 3, color: "#7a6e64", marginBottom: 16 }}>YOUR PLAN INCLUDES</div>
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
          {(planFeatures[plan] || planFeatures.free).map(function(f) {
            return (
              <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#4a3f38" }}>
                <span style={{ color: planColor, fontWeight: 700 }}>✓</span>{f}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Quick Actions */}
      <div className="dash-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <button onClick={props.onAnalyse} style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,0.28)", borderRadius: 16, padding: "20px 24px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 16, fontWeight: 500, color: "#2c2420", marginBottom: 4 }}>Analyse Resume</div>
          <div style={{ fontSize: 11, color: "#7a6e64" }}>Check your ATS score now</div>
        </button>
        <button onClick={props.onUpgrade} style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,0.28)", borderRadius: 16, padding: "20px 24px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⭐</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 16, fontWeight: 500, color: "#2c2420", marginBottom: 4 }}>{plan === "free" ? "Upgrade Plan" : "View Plans"}</div>
          <div style={{ fontSize: 11, color: "#7a6e64" }}>{plan === "free" ? "Unlock all features" : "See all available plans"}</div>
        </button>
      </div>
    </div>
  );
}

export default function DashboardView(props) {
  var user = props.user;
  var userPlan = props.plan;
  return (
    <div style={S.root}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap'); @keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; } body { background: #f7f3ee; }`}</style>
      <header style={S.header}>
        <div style={S.logoRow} onClick={props.onHome}>
          <div style={S.logoMark}>
            <svg viewBox="0 0 22 22" fill="none" width={17} height={17}>
              <path d="M11 2L19 6.5V15.5L11 20L3 15.5V6.5L11 2Z" stroke="#f7f3ee" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M11 7V11L14 13" stroke="#c9963a" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx={11} cy={11} r={1.8} fill="#c9963a" />
            </svg>
          </div>
          <div>
            <div style={S.logoName}>SYNTRIX</div>
            <div style={S.logoTag}>AI RESUME INTELLIGENCE</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={props.onHome}>Home</button>
          <button style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={props.onAnalyse}>Analyse</button>
          <button style={{ ...S.hBtn, background: "#2c2420", color: "#f7f3ee", fontWeight: 600 }} onClick={props.onSignOut}>Sign Out</button>
        </div>
      </header>
      {user && <PlanBanner plan={userPlan} user={user} onSignOut={props.onSignOut} />}
      <DashboardPanel user={user} plan={userPlan} onAnalyse={props.onAnalyse} onUpgrade={props.onUpgrade} />
    </div>
  );
}
